import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowLeft, FiSend } from "react-icons/fi";

// Reusable Components
import { PageHeader } from "../../components/common";

// Page-specific Components
import FrequencySelector from "../../components/schedule/FrequencySelector";
import DateCalendar from "../../components/schedule/DateCalendar";
import ReviewSummary from "../../components/schedule/ReviewSummary";
import AutomatedWorkflowConfig from "../../components/schedule/AutomatedWorkflowConfig";
import WorkflowCreatedModal from "../../components/schedule/WorkflowCreatedModal";

// Hooks & Services
import { useSubscription } from "../../hooks/useSubscription";
import { workflowService } from "../../services/workflowService";
import { integrationsService } from "../../services/integrationsService";

// Styles
import "../../styles/schedule.css";

// Type-safe icon wrappers
const ArrowLeftIcon = FiArrowLeft as React.FC<React.SVGProps<SVGSVGElement>>;
const SendIcon = FiSend as React.FC<React.SVGProps<SVGSVGElement>>;

// Helper function to convert TipTap JSON to HTML
const generateHTMLFromContent = (json: any): string => {
  // Simple recursive function to convert TipTap JSON to HTML
  const nodeToHTML = (node: any): string => {
    if (node.type === 'text') {
      let text = node.text || '';
      if (node.marks) {
        node.marks.forEach((mark: any) => {
          if (mark.type === 'bold') text = `<strong>${text}</strong>`;
          if (mark.type === 'italic') text = `<em>${text}</em>`;
          if (mark.type === 'underline') text = `<u>${text}</u>`;
        });
      }
      return text;
    }
    
    const children = node.content ? node.content.map(nodeToHTML).join('') : '';
    
    switch (node.type) {
      case 'paragraph': return `<p>${children}</p>`;
      case 'heading': return `<h${node.attrs?.level || 1}>${children}</h${node.attrs?.level || 1}>`;
      case 'bulletList': return `<ul>${children}</ul>`;
      case 'orderedList': return `<ol>${children}</ol>`;
      case 'listItem': return `<li>${children}</li>`;
      case 'blockquote': return `<blockquote>${children}</blockquote>`;
      case 'doc': return children;
      default: return children;
    }
  };
  
  return nodeToHTML(json);
};

// Frequency types
export type FrequencyType = "one-time" | "weekly" | "monthly";

// Insight type for automated workflows
export interface InsightType {
  id: string;
  title: string;
  description: string;
  provider: string;
}

// Hardcoded insight types
const INSIGHT_TYPES: InsightType[] = [
  { id: "revenue-metrics", title: "Revenue metrics", description: "Via Stripe", provider: "stripe" },
  { id: "engineering-velocity", title: "Engineering velocity", description: "Via Github", provider: "github" },
  { id: "sprint-completion", title: "Sprint completion", description: "Via Clickup", provider: "clickup" },
  { id: "team-collaboration", title: "Team collaboration", description: "Via Slack", provider: "slack" },
  { id: "pr-activity", title: "PR activity", description: "Via Github", provider: "github" },
  { id: "customer-feedback", title: "Customer feedback", description: "Via Slack", provider: "slack" },
];

interface Integration {
  id: string;
  provider: string;
  name: string;
}

/**
 * ScheduleReviewPage - Final step in update flow
 */
const ScheduleReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPro, features } = useSubscription();

  // Get recipient IDs and subject from URL
  const recipientIds = useMemo(() => {
    const selected = searchParams.get("selected") || searchParams.get("recipients");
    return selected ? selected.split(",").filter(Boolean) : [];
  }, [searchParams]);

  const emailSubject = searchParams.get("subject") || "";

  // Frequency & date state
  const [selectedFrequency, setSelectedFrequency] = useState<FrequencyType>("one-time");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Automated workflow state
  const [saveAsWorkflow, setSaveAsWorkflow] = useState(false);
  const [workflowName, setWorkflowName] = useState("");
  const [selectedInsightTypes, setSelectedInsightTypes] = useState<string[]>([]);

  // Integration state
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);

  // Loading state
  const [isScheduling, setIsScheduling] = useState(false);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdWorkflowData, setCreatedWorkflowData] = useState<{
    name: string;
    subject: string;
    recipientCount: number;
    frequency: string;
    firstSendDate: string;
  } | null>(null);

  // Load integrations on mount
  useEffect(() => {
    loadIntegrations();
  }, []);

  // Auto-enable workflow config for weekly/monthly
  useEffect(() => {
    if (selectedFrequency === "weekly" || selectedFrequency === "monthly") {
      setSaveAsWorkflow(true);
      if (!workflowName) {
        setWorkflowName(
          selectedFrequency === "weekly" ? "Weekly Investor Update" : "Monthly Investor Update"
        );
      }
    }
  }, [selectedFrequency]);

  const loadIntegrations = async () => {
    try {
      const userIntegrations = await integrationsService.getUserIntegrations();
      const mapped = userIntegrations.map((int: any) => ({
        id: int.id,
        provider: int.provider,
        name: int.provider.charAt(0).toUpperCase() + int.provider.slice(1),
      }));
      setIntegrations(mapped);
      // Pre-select all integrations
      setSelectedDataSources(mapped.map((i: Integration) => i.provider));
    } catch (err) {
      console.error("Failed to load integrations:", err);
    }
  };

  // Check if schedule button should be enabled
  const canSchedule = useMemo(() => {
    // Must have recipients
    if (recipientIds.length === 0) return false;
    // Must have a date selected
    if (!selectedDate) return false;
    // If weekly/monthly, must have workflow name
    if ((selectedFrequency === "weekly" || selectedFrequency === "monthly") && !workflowName.trim()) {
      return false;
    }
    return true;
  }, [recipientIds, selectedDate, selectedFrequency, workflowName]);

  // Handle frequency change
  const handleFrequencyChange = (frequency: FrequencyType) => {
    // Check Pro access for weekly/monthly
    if (frequency === "weekly" && !features.canUseWeeklyEmails) return;
    if (frequency === "monthly" && !features.canUseMonthlyEmails) return;
    setSelectedFrequency(frequency);
  };

  // Generate cron expression based on frequency and date
  const generateCronExpression = (scheduledDateTime: Date, frequency: FrequencyType): string => {
    const hour = scheduledDateTime.getHours();
    const minute = scheduledDateTime.getMinutes();
    const dayOfMonth = scheduledDateTime.getDate();
    const month = scheduledDateTime.getMonth() + 1; // JS months are 0-indexed
    const dayOfWeek = scheduledDateTime.getDay();

    switch (frequency) {
      case "one-time":
        // Specific date: minute hour dayOfMonth month *
        return `${minute} ${hour} ${dayOfMonth} ${month} *`;
      case "weekly":
        // Every week on same day: minute hour * * dayOfWeek
        return `${minute} ${hour} * * ${dayOfWeek}`;
      case "monthly":
        // Every month on same day: minute hour dayOfMonth * *
        return `${minute} ${hour} ${dayOfMonth} * *`;
      default:
        return `${minute} ${hour} ${dayOfMonth} ${month} *`;
    }
  };

  // Handle schedule send
  const handleScheduleSend = async () => {
    if (!canSchedule || !selectedDate) return;

    setIsScheduling(true);
    try {
      // Retrieve compose data from localStorage
      const composeStateStr = localStorage.getItem('compose_temp_state');
      let retrievedEmailSubject = emailSubject;
      let emailBody = "";
      let composeSelectedIntegrations: string[] = [];
      let composeSelectedInsights: any[] = [];
      
      if (composeStateStr) {
        try {
          const composeState = JSON.parse(composeStateStr);
          retrievedEmailSubject = composeState.subject || retrievedEmailSubject;
          composeSelectedIntegrations = composeState.selectedIntegrations || [];
          composeSelectedInsights = composeState.selectedInsights || [];
          
          // Convert TipTap JSON content to HTML
          if (composeState.content) {
            emailBody = generateHTMLFromContent(composeState.content);
          }
          
          console.log('📧 Retrieved compose data from localStorage:', {
            subject: retrievedEmailSubject,
            hasBody: !!emailBody,
            integrations: composeSelectedIntegrations.length,
            insights: composeSelectedInsights.length,
          });
        } catch (error) {
          console.error('Failed to parse compose state:', error);
        }
      }
      
      // Calculate the actual scheduled date/time (9 AM on selected date)
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(9, 0, 0, 0);
      
      // If scheduling for today and time is in the past, schedule for 1 minute from now
      if (scheduledDateTime < new Date()) {
        scheduledDateTime.setTime(new Date().getTime() + 60000);
        console.log('⚠️ Scheduled time was in the past, adjusted to:', scheduledDateTime);
      }

      // Generate cron expression based on the adjusted scheduledDateTime
      const cronExpression = generateCronExpression(scheduledDateTime, selectedFrequency);
      
      // Format time string for display
      const timeString = `${scheduledDateTime.getHours().toString().padStart(2, '0')}:${scheduledDateTime.getMinutes().toString().padStart(2, '0')}`;

      // Map selectedInsights to full insight objects with required shape
      let mappedInsights: any[] = [];
      
      if (saveAsWorkflow) {
        // Map selectedInsightTypes (string IDs) to full insight objects
        mappedInsights = selectedInsightTypes.map((insightTypeId) => {
          // Find the insight type definition from INSIGHT_TYPES
          const insightType = INSIGHT_TYPES.find((type) => type.id === insightTypeId);
          
          if (!insightType) {
            console.warn(`Insight type not found for ID: ${insightTypeId}`);
            return null;
          }
          
          return {
            id: insightType.id,
            title: insightType.title,
            description: insightType.description,
            type: 'automated', // Workflow-generated insights
            integration: insightType.provider,
            data: {
              provider: insightType.provider,
              insightType: insightType.id,
            },
            selected: true,
          };
        }).filter(Boolean); // Remove any null entries
      } else {
        // Map composeSelectedInsights to ensure they have all required fields
        // Validate that insights have database IDs
        mappedInsights = (composeSelectedInsights || []).map((insight: any) => {
          // Check if this insight has a valid database ID
          const hasValidId = insight.id && 
            !insight.id.startsWith('insight_temp_') && 
            !insight.id.startsWith('temp_') &&
            !/_\d+$/.test(insight.id);
          
          if (!hasValidId) {
            console.warn('⚠️ Insight missing valid database ID:', insight.title, 'ID:', insight.id);
            console.warn('This insight may not display properly in VC dashboard');
          } else {
            console.log('✅ Insight has valid database ID:', insight.title, 'ID:', insight.id);
          }
          
          return {
            id: insight.id || `insight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            title: insight.title || 'Untitled Insight',
            description: typeof insight.summary === 'string' 
              ? insight.summary 
              : insight.summary?.narrative || insight.summary?.headline || '',
            type: insight.type || 'manual',
            integration: insight.data_sources?.[0] || 'manual',
            data: {
              summary: insight.summary,
              data_sources: insight.data_sources || [],
              created_at: insight.created_at,
            },
            selected: true,
          };
        });
        
        console.log(`📊 Mapped ${mappedInsights.length} insights for workflow:`, 
          mappedInsights.map(i => ({ id: i.id, title: i.title })));
      }

      // Create workflow
      const workflowData = {
        name: workflowName || `Update - ${selectedDate.toLocaleDateString()}`,
        description: `Scheduled ${selectedFrequency} update`,
        recipientIds,
        selectedIntegrations: selectedDataSources.length > 0 ? selectedDataSources : composeSelectedIntegrations,
        selectedInsights: mappedInsights, // Use fully-shaped insight objects
        generatedEmail: retrievedEmailSubject && emailBody ? {
          subject: retrievedEmailSubject,
          body: emailBody,
        } : undefined,
        schedule: {
          enabled: true,
          cron: cronExpression,
          frequency: selectedFrequency,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
          time: timeString,
          days: selectedFrequency === "weekly" ? [String(selectedDate.getDay())] : [],
          scheduledDate: scheduledDateTime.toISOString(),
        },
        next_execution: scheduledDateTime.toISOString(),
        notifications: {
          onSuccess: true,
          onFailure: true,
        },
      };
      
      console.log('🚀 Creating workflow with data:', workflowData);

      await workflowService.createCustomWorkflow(workflowData);
      
      // Clear localStorage after successful creation
      localStorage.removeItem('compose_temp_state');
      console.log('✅ Cleared compose state from localStorage');

      // Format frequency for display
      const frequencyDisplay = selectedFrequency === "one-time" 
        ? "One-time" 
        : selectedFrequency.charAt(0).toUpperCase() + selectedFrequency.slice(1);

      // Format date for display
      const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: "short",
        month: "short",
        day: "numeric",
      };
      const firstSendDisplay = selectedDate.toLocaleDateString("en-US", dateOptions);

      // Show success modal
      setCreatedWorkflowData({
        name: workflowData.name,
        subject: retrievedEmailSubject || "[Company Name] Board Update - [Date]",
        recipientCount: recipientIds.length,
        frequency: frequencyDisplay,
        firstSendDate: firstSendDisplay,
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to schedule send:", error);
      alert("Failed to schedule send. Please try again.");
    } finally {
      setIsScheduling(false);
    }
  };

  // Format frequency display
  const frequencyDisplay = useMemo(() => {
    switch (selectedFrequency) {
      case "one-time":
        return "One Time";
      case "weekly":
        return "Weekly";
      case "monthly":
        return "Monthly";
      default:
        return "One Time";
    }
  }, [selectedFrequency]);

  // Format date display
  const dateDisplay = useMemo(() => {
    if (!selectedDate) return "Not selected";
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      month: "short",
      day: "numeric",
    };
    return selectedDate.toLocaleDateString("en-US", options);
  }, [selectedDate]);

  return (
    <div className="su-schedule-page">
      {/* Page Header */}
      <PageHeader
        title="Schedule & Review"
        subtitle="Set frequency and date for your update"
      />

      {/* Main Content - Two Column Grid */}
      <div className="su-schedule-grid">
        {/* Left Column - Frequency Selector */}
        <FrequencySelector
          selectedFrequency={selectedFrequency}
          onFrequencyChange={handleFrequencyChange}
          canUseWeekly={features.canUseWeeklyEmails}
          canUseMonthly={features.canUseMonthlyEmails}
        />

        {/* Right Column - Calendar */}
        <DateCalendar
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
        />
      </div>

      {/* Automated Workflow Config (for weekly/monthly) */}
      {(selectedFrequency === "weekly" || selectedFrequency === "monthly") && (
        <AutomatedWorkflowConfig
          enabled={saveAsWorkflow}
          onToggle={setSaveAsWorkflow}
          workflowName={workflowName}
          onWorkflowNameChange={setWorkflowName}
          integrations={integrations}
          selectedDataSources={selectedDataSources}
          onDataSourceToggle={(provider) => {
            setSelectedDataSources((prev) =>
              prev.includes(provider)
                ? prev.filter((p) => p !== provider)
                : [...prev, provider]
            );
          }}
          insightTypes={INSIGHT_TYPES}
          selectedInsightTypes={selectedInsightTypes}
          onInsightTypeToggle={(id) => {
            setSelectedInsightTypes((prev) =>
              prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
            );
          }}
        />
      )}

      {/* Review Summary */}
      <ReviewSummary
        recipientCount={recipientIds.length}
        frequency={frequencyDisplay}
        scheduledDate={dateDisplay}
        type={selectedFrequency === "one-time" ? "One-time" : "Recurring"}
      />

      {/* Bottom Navigation */}
      <div className="su-schedule-bottom-nav">
        <button
          className="su-btn-secondary su-schedule-back-btn"
          onClick={() => navigate(`/updates/recipients?selected=${recipientIds.join(",")}`)}
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>BACK TO RECIPIENTS</span>
        </button>

        <button
          className={`su-btn-primary su-schedule-send-btn ${!canSchedule ? "su-btn-disabled" : ""}`}
          disabled={!canSchedule || isScheduling}
          onClick={handleScheduleSend}
        >
          <SendIcon className="w-4 h-4" />
          <span>{isScheduling ? "SCHEDULING..." : "SCHEDULE SEND"}</span>
        </button>
      </div>

      {/* Success Modal */}
      {createdWorkflowData && (
        <WorkflowCreatedModal
          isOpen={showSuccessModal}
          workflowName={createdWorkflowData.name}
          subject={createdWorkflowData.subject}
          recipientCount={createdWorkflowData.recipientCount}
          frequency={createdWorkflowData.frequency}
          firstSendDate={createdWorkflowData.firstSendDate}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
    </div>
  );
};

export default ScheduleReviewPage;

